import { call, put, select, takeLatest } from "redux-saga/effects";
import {
  fetchAllImages,
  getImageDatesApi,
  getImageDetailApi,
} from "../../../api/fieldDataApi";
import {
  buildImageDateRange,
  FIELD_STATUS_ALL_CLASSIFICATIONS,
  getClassificationCounts,
  getLatestImageDate,
  normalizeFieldDataImages,
  normalizeImageDates,
} from "../../../utils/fieldDataDiseaseStatusUtils";
import { filterItemsWithinPolygon } from "../../../utils/geoUtils";
import {
  fetchDiseaseStatusFailure,
  fetchDiseaseStatusImageDetailFailure,
  fetchDiseaseStatusImageDetailStart,
  fetchDiseaseStatusImageDetailSuccess,
  fetchDiseaseStatusStart,
  fetchDiseaseStatusSuccess,
} from "../../../store/features/customer/diseaseStatus/diseaseStatusSlice";

function* resolveLatestImageDateRange(fieldFilter) {
  const imageDatesResponse = yield call(getImageDatesApi, fieldFilter);
  const dates = normalizeImageDates(imageDatesResponse?.data);
  const latestDate = getLatestImageDate(imageDatesResponse?.data);

  return {
    dates,
    latestDate,
    ...buildImageDateRange(latestDate),
  };
}

function* fetchDiseaseStatusSaga(action) {
  try {
    const fieldFilters =
      action.payload?.fieldFilters ||
      (action.payload?.fieldUuid
        ? [{ field_uuid: action.payload.fieldUuid }]
        : []);
    const classification = action.payload?.classification;
    const fieldPolygon = action.payload?.fieldPolygon;
    const selectedClassification =
      classification && classification !== FIELD_STATUS_ALL_CLASSIFICATIONS
        ? classification
        : null;
    const previousClassificationCounts = yield select(
      (state) => state.diseaseStatus?.data?.classificationCounts || {},
    );

    if (fieldFilters.length === 0) {
      yield put(
        fetchDiseaseStatusSuccess({
          dates: [],
          latestDate: null,
          startTime: null,
          endTime: null,
          selectedClassification: FIELD_STATUS_ALL_CLASSIFICATIONS,
          images: [],
          classificationCounts: {},
          fieldFilter: null,
        }),
      );
      return;
    }

    let emptyResult = null;
    let lastError = null;

    for (const fieldFilter of fieldFilters) {
      try {
        const { dates, latestDate, startTime, endTime } = yield call(
          resolveLatestImageDateRange,
          fieldFilter,
        );

        if (!startTime || !endTime) {
          emptyResult = {
            dates,
            latestDate,
            startTime: null,
            endTime: null,
            selectedClassification:
              selectedClassification || FIELD_STATUS_ALL_CLASSIFICATIONS,
            images: [],
            classificationCounts: previousClassificationCounts,
            fieldFilter,
          };
          continue;
        }

        const imagesResponse = yield call(fetchAllImages, {
          fieldFilter,
          startTime,
          endTime,
          classification: selectedClassification,
        });
        const images = filterItemsWithinPolygon(
          normalizeFieldDataImages(imagesResponse),
          fieldPolygon,
        );
        const nextClassificationCounts =
          selectedClassification &&
          Object.keys(previousClassificationCounts).length > 0
            ? previousClassificationCounts
            : getClassificationCounts(images);

        yield put(
          fetchDiseaseStatusSuccess({
            dates,
            latestDate,
            startTime,
            endTime,
            selectedClassification:
              selectedClassification || FIELD_STATUS_ALL_CLASSIFICATIONS,
            images,
            classificationCounts: nextClassificationCounts,
            fieldFilter,
          }),
        );
        return;
      } catch (error) {
        lastError = error;
      }
    }

    if (emptyResult) {
      yield put(fetchDiseaseStatusSuccess(emptyResult));
      return;
    }

    if (lastError) {
      throw lastError;
    }

    yield put(
      fetchDiseaseStatusSuccess({
        dates: [],
        latestDate: null,
        startTime: null,
        endTime: null,
        selectedClassification:
          selectedClassification || FIELD_STATUS_ALL_CLASSIFICATIONS,
        images: [],
        classificationCounts: {},
        fieldFilter: null,
      }),
    );
  } catch (error) {
    yield put(fetchDiseaseStatusFailure(error.message || String(error)));
  }
}

function* fetchDiseaseStatusImageDetailSaga(action) {
  const imageId = action.payload?.imageId;

  try {
    if (!imageId) {
      throw new Error("ID immagine non disponibile");
    }

    const detail = yield call(getImageDetailApi, imageId);
    yield put(fetchDiseaseStatusImageDetailSuccess({ imageId, detail }));
  } catch (error) {
    yield put(
      fetchDiseaseStatusImageDetailFailure({
        imageId,
        error: error.message || String(error),
      }),
    );
  }
}

export default function* diseaseStatusSaga() {
  yield takeLatest(fetchDiseaseStatusStart.type, fetchDiseaseStatusSaga);
  yield takeLatest(
    fetchDiseaseStatusImageDetailStart.type,
    fetchDiseaseStatusImageDetailSaga,
  );
}
